import { Link, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { LogOut, Menu } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { CandidateSidebar } from "./Sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "../ui/use-toast";
import { LevelBadge } from "../gamification/LevelBadge";
import { NotificationBell } from "../notifications/NotificationBell";
import { useProfile } from "@/hooks/useProfile";
import { useQueryClient } from "@tanstack/react-query";

export function CandidateHeader() {
  const isMobile = useIsMobile();
  const { profile } = useProfile();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleLogout = async () => {
    try {
      // Limpar todo o cache do React Query antes do logout
      queryClient.clear();
      await supabase.auth.signOut();
      navigate('/');
      toast({
        title: "Logout realizado",
        description: "Você foi desconectado com sucesso.",
      });
    } catch (error) {
      console.error('Error logging out:', error);
      toast({
        title: "Erro ao sair",
        description: "Ocorreu um erro ao tentar fazer logout.",
        variant: "destructive",
      });
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-black text-white overflow-hidden">
      <div className="flex h-16 items-center justify-between px-3 sm:px-4 max-w-full">
        <div className="flex items-center gap-2 min-w-0 flex-shrink">
          {isMobile && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:text-white/80 shrink-0">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[280px]">
                <CandidateSidebar isMobileSheet={true} />
              </SheetContent>
            </Sheet>
          )}

          <Link to="/candidate/dashboard" className="flex items-center shrink-0">
            <img
              src="/lovable-uploads/logo-horizontal.png"
              alt="Data Hire Club - Dashboard de candidatos"
              className="h-10 sm:h-14 md:h-16 w-auto"
              width="200"
              height="64"
            />
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {profile?.user_id && (
            <div className="hidden sm:block">
              <LevelBadge userId={profile.user_id} showPoints />
            </div>
          )}
          <NotificationBell />
          <Avatar className="h-8 w-8">
            <AvatarImage
              src={profile?.photoUrl || undefined}
              className="object-cover"
            />
            <AvatarFallback className="bg-white/20 text-white">
              {profile?.full_name ? getInitials(profile.full_name) : "?"}
            </AvatarFallback>
          </Avatar>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:text-white/80"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </header>
  );
}